import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, Wallet, Briefcase, Home as HomeIcon, Building2,
  RefreshCw, DollarSign, Receipt, PiggyBank, Percent, Search,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { format, subDays, subMonths } from 'date-fns';

const fmtINR = (n) => {
  const num = Number(n) || 0;
  const sign = num < 0 ? '-' : '';
  return sign + '₹' + Math.abs(num).toLocaleString('en-IN');
};

const DATE_RANGES = [
  { value: 'all', label: 'All Time' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'last3months', label: 'Last 3 Months' },
  { value: 'last6months', label: 'Last 6 Months' },
  { value: 'last1year', label: 'Last 1 Year' },
];

const inRange = (dateStr, range) => {
  if (range === 'all' || !dateStr) return true;
  const d = new Date(dateStr);
  const now = new Date();
  switch (range) {
    case 'thisMonth': return d >= new Date(now.getFullYear(), now.getMonth(), 1);
    case 'last30days': return d >= subDays(now, 30);
    case 'last3months': return d >= subMonths(now, 3);
    case 'last6months': return d >= subMonths(now, 6);
    case 'last1year': return d >= subMonths(now, 12);
    default: return true;
  }
};

const ProfitabilityDashboard = () => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedClient, setExpandedClient] = useState(null);

  // ── ONLY approved quotations + their linked expenses ──
  const { data: archQuotes = [], isLoading: l1, refetch: r1 } = useQuery({
    queryKey: ['prof_arch_quotes'],
    queryFn: () => pb.collection('quotations').getFullList({
      filter: 'approvalStatus = "approved"',
      sort: '-created',
    }).catch(() => []),
    enabled: !!user,
  });

  const { data: intQuotes = [], isLoading: l2, refetch: r2 } = useQuery({
    queryKey: ['prof_int_quotes'],
    queryFn: () => pb.collection('interior_quotations').getFullList({
      filter: 'approvalStatus = "approved"',
      sort: '-created',
    }).catch(() => []),
    enabled: !!user,
  });

  const { data: expenses = [], isLoading: l3, refetch: r3 } = useQuery({
    queryKey: ['prof_oc_project_expenses'],
    queryFn: () => pb.collection('oc_project_expenses').getFullList({ sort: '-date,-created' }).catch(() => []),
    enabled: !!user,
  });

  const { data: officeExpenses = [], isLoading: l4, refetch: r4 } = useQuery({
    queryKey: ['prof_oc_office_expenses'],
    queryFn: () => pb.collection('oc_office_expenses').getFullList({ sort: '-expenseDate,-created' }).catch(() => []),
    enabled: !!user,
  });

  const isLoading = l1 || l2 || l3 || l4;

  const refreshAll = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try { await Promise.all([r1(), r2(), r3(), r4()]); } catch {}
    finally { setTimeout(() => setRefreshing(false), 500); }
  };

  const model = useMemo(() => {
    // ── Map expenses by quotationId ──
    const expensesByArchQuote = {};
    const expensesByIntQuote = {};
    expenses.forEach(e => {
      if (!inRange(e.date || e.created, dateRange)) return;
      const amt = Number(e.amount) || 0;
      if (e.quotationType === 'architecture' && e.quotation) {
        expensesByArchQuote[e.quotation] = (expensesByArchQuote[e.quotation] || 0) + amt;
      }
      if (e.quotationType === 'interior' && e.interiorQuotation) {
        expensesByIntQuote[e.interiorQuotation] = (expensesByIntQuote[e.interiorQuotation] || 0) + amt;
      }
    });

    // ── Build earning + expense rows per quotation ──
    const archEarnings = archQuotes
      .filter(q => inRange(q.created, dateRange))
      .map(q => ({
        id: q.id,
        quoteNumber: q.quoteNumber,
        clientName: q.clientName || 'Unknown',
        amount: Number(q.total) || 0,
        status: q.status,
        created: q.created,
        expense: expensesByArchQuote[q.id] || 0,
        type: 'architecture',
      }));

    const intEarnings = intQuotes
      .filter(q => inRange(q.created, dateRange))
      .map(q => ({
        id: q.id,
        quoteNumber: q.quotationNumber || q.quoteNumber || q.id.slice(0, 8),
        clientName: q.clientName || q.projectName || 'Unknown',
        amount: Number(q.total) || 0,
        status: q.status,
        created: q.created,
        expense: expensesByIntQuote[q.id] || 0,
        type: 'interior',
      }));

    const sum = (arr, key) => arr.reduce((s, r) => s + (Number(r[key]) || 0), 0);

    const archEarning = sum(archEarnings, 'amount');
    const intEarning = sum(intEarnings, 'amount');
    const totalEarning = archEarning + intEarning;

    const archExpense = sum(archEarnings, 'expense');
    const intExpense = sum(intEarnings, 'expense');
    const totalProjectExpense = archExpense + intExpense;

    const officeExpenseTotal = officeExpenses
      .filter(e => inRange(e.expenseDate || e.created, dateRange))
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);

    const archProfit = archEarning - archExpense;
    const intProfit = intEarning - intExpense;
    const totalProjectProfit = totalEarning - totalProjectExpense;
    const netOfficeProfit = totalProjectProfit - officeExpenseTotal;

    return {
      archEarnings, intEarnings,
      archEarning, intEarning, totalEarning,
      archExpense, intExpense, totalProjectExpense, officeExpenseTotal,
      archProfit, intProfit, totalProjectProfit, netOfficeProfit,
      officeExpenseCount: officeExpenses.filter(e => inRange(e.expenseDate || e.created, dateRange)).length,
    };
  }, [archQuotes, intQuotes, expenses, officeExpenses, dateRange]);

  // ── Client-wise aggregation ──
  const clientRows = useMemo(() => {
    const map = new Map();
    [...model.archEarnings, ...model.intEarnings].forEach(q => {
      const key = (q.clientName || 'Unknown').trim();
      if (!map.has(key)) {
        map.set(key, { clientName: key, earning: 0, expense: 0, quotes: [] });
      }
      const row = map.get(key);
      row.earning += q.amount;
      row.expense += q.expense;
      row.quotes.push(q);
    });
    return Array.from(map.values()).map(r => ({
      ...r,
      profit: r.earning - r.expense,
      margin: r.earning > 0 ? ((r.earning - r.expense) / r.earning * 100) : 0,
    })).sort((a, b) => b.profit - a.profit);
  }, [model]);

  const filteredClients = useMemo(() => {
    if (!search) return clientRows;
    const q = search.toLowerCase();
    return clientRows.filter(c => c.clientName.toLowerCase().includes(q));
  }, [clientRows, search]);

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="relative rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-8 shadow-xl">
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <Percent className="w-8 h-8" /> Profitability Dashboard
            </h1>
            <p className="text-white/80">Approved quotation earnings − linked expenses = profit</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-44 bg-white/10 text-white border-white/30 z-20"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[100]">
                {DATE_RANGES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={refreshAll} disabled={refreshing}
              className="bg-white/10 text-white border-white/30 hover:bg-white/20">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <BigStat icon={DollarSign} label="Turn Over" value={fmtINR(model.totalEarning)}
          sub={`Arch ${fmtINR(model.archEarning)} • Int ${fmtINR(model.intEarning)}`} color="from-blue-500 to-indigo-600" />
        <BigStat icon={Receipt} label="Project Expenses" value={fmtINR(model.totalProjectExpense)}
          sub={`Arch ${fmtINR(model.archExpense)} • Int ${fmtINR(model.intExpense)}`} color="from-amber-500 to-orange-600" />
        <BigStat icon={TrendingUp} label="Office Earning" value={fmtINR(model.totalProjectProfit)}
          sub={model.totalEarning > 0 ? `${((model.totalProjectProfit / model.totalEarning) * 100).toFixed(1)}% margin` : '—'}
          color={model.totalProjectProfit >= 0 ? 'from-emerald-500 to-teal-600' : 'from-red-500 to-rose-600'} />
        <BigStat icon={PiggyBank} label="Net Office Profit" value={fmtINR(model.netOfficeProfit)}
          sub={`After ${fmtINR(model.officeExpenseTotal)} office expense`}
          color={model.netOfficeProfit >= 0 ? 'from-green-500 to-emerald-600' : 'from-red-500 to-rose-600'} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CategoryCard icon={HomeIcon} title="Architecture Projects" accent="indigo"
          earning={model.archEarning} expense={model.archExpense} profit={model.archProfit}
          expenseCount={model.archEarnings.filter(q => q.expense > 0).length} quoteCount={model.archEarnings.length} />
        <CategoryCard icon={Briefcase} title="Interior Projects" accent="pink"
          earning={model.intEarning} expense={model.intExpense} profit={model.intProfit}
          expenseCount={model.intEarnings.filter(q => q.expense > 0).length} quoteCount={model.intEarnings.length} />
      </div>

      <Card className="border-0 shadow-xl">
        <CardContent className="p-0">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              <h2 className="font-bold text-slate-800">Office Expenses</h2>
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">
                {model.officeExpenseCount} entries
              </span>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500 uppercase font-semibold">Total</div>
              <div className="text-lg font-black text-purple-700">{fmtINR(model.officeExpenseTotal)}</div>
            </div>
          </div>
          {model.officeExpenseCount === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No office expenses in this period.</div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="p-3 text-left font-semibold text-slate-600">Date</th>
                    <th className="p-3 text-left font-semibold text-slate-600">Category</th>
                    <th className="p-3 text-left font-semibold text-slate-600">Description</th>
                    <th className="p-3 text-right font-semibold text-slate-600">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {officeExpenses.filter(e => inRange(e.expenseDate || e.created, dateRange)).map(e => (
                    <tr key={e.id} className="hover:bg-slate-50">
                      <td className="p-3 text-slate-600">{e.expenseDate ? format(new Date(e.expenseDate), 'dd MMM yyyy') : '—'}</td>
                      <td className="p-3"><span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded capitalize">{e.category}</span></td>
                      <td className="p-3 text-slate-700 max-w-md truncate">{e.description}</td>
                      <td className="p-3 text-right font-bold text-slate-800">{fmtINR(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0">
          <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <div>
              <h2 className="font-bold text-slate-800">Client-wise Profitability</h2>
              <p className="text-xs text-slate-500">Click a row to expand quotations</p>
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Search client..."
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          {filteredClients.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No client data found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                  <tr>
                    <th className="p-3 text-left w-8"></th>
                    <th className="p-3 text-left font-semibold text-indigo-700">Client</th>
                    <th className="p-3 text-right font-semibold text-blue-700">Earning</th>
                    <th className="p-3 text-right font-semibold text-amber-700">Expense</th>
                    <th className="p-3 text-right font-semibold text-emerald-700">Profit</th>
                    <th className="p-3 text-right font-semibold text-indigo-700">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredClients.map((row, idx) => {
                    const isOpen = expandedClient === row.clientName;
                    const profitable = row.profit >= 0;
                    return (
                      <tbody key={idx} style={{ display: 'contents' }}>
                        <tr onClick={() => setExpandedClient(isOpen ? null : row.clientName)}
                          className="hover:bg-indigo-50/50 cursor-pointer">
                          <td className="p-3 text-slate-400">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                          <td className="p-3 font-medium text-slate-800">{row.clientName}</td>
                          <td className="p-3 text-right font-semibold text-blue-700">{fmtINR(row.earning)}</td>
                          <td className="p-3 text-right font-semibold text-amber-700">{fmtINR(row.expense)}</td>
                          <td className={`p-3 text-right font-bold ${profitable ? 'text-emerald-700' : 'text-red-700'}`}>{fmtINR(row.profit)}</td>
                          <td className={`p-3 text-right font-semibold ${profitable ? 'text-emerald-600' : 'text-red-600'}`}>{row.margin.toFixed(1)}%</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50">
                            <td colSpan={6} className="p-4">
                              <div className="text-xs">
                                <div className="font-semibold text-slate-600 mb-2">Quotations & Expenses:</div>
                                {row.quotes.length === 0 ? (
                                  <div className="text-slate-400">No quotations</div>
                                ) : (
                                  <div className="space-y-1">
                                    {row.quotes.map((q, qi) => (
                                      <div key={qi} className="bg-white rounded-lg px-3 py-2 border border-slate-200">
                                        <div className="flex justify-between items-center">
                                          <div className="flex items-center gap-2">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${q.type === 'architecture' ? 'bg-indigo-100 text-indigo-700' : 'bg-pink-100 text-pink-700'}`}>{q.type}</span>
                                            <span className="font-mono text-slate-700">{q.quoteNumber}</span>
                                            <span className="text-slate-400">{q.status}</span>
                                          </div>
                                          <div className="flex items-center gap-4">
                                            <div className="text-right">
                                              <div className="text-[10px] text-slate-400 uppercase">Quote</div>
                                              <div className="font-bold text-slate-800">{fmtINR(q.amount)}</div>
                                            </div>
                                            <div className="text-right">
                                              <div className="text-[10px] text-slate-400 uppercase">Expense</div>
                                              <div className={`font-bold ${q.expense > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{fmtINR(q.expense)}</div>
                                            </div>
                                            <div className="text-right">
                                              <div className="text-[10px] text-slate-400 uppercase">Profit</div>
                                              <div className={`font-bold ${(q.amount - q.expense) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtINR(q.amount - q.expense)}</div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <CardContent className="p-6">
          <h3 className="text-sm font-bold text-white/60 uppercase tracking-wide mb-4">Final Calculation</h3>
          <div className="space-y-3">
            <Row label="Turn Over (Arch + Interior)" value={fmtINR(model.totalEarning)} />
            <Row label="− Project Expenses" value={'- ' + fmtINR(model.totalProjectExpense)} muted />
            <div className="border-t border-white/10 pt-3">
              <Row label="= Office Earning" value={fmtINR(model.totalProjectProfit)} bold highlight={model.totalProjectProfit >= 0} />
            </div>
            <Row label="− Office Expenses" value={'- ' + fmtINR(model.officeExpenseTotal)} muted />
            <div className="border-t-2 border-white/20 pt-3 mt-3">
              <div className="flex justify-between items-center">
                <div className="text-base font-bold text-white">= NET OFFICE PROFIT</div>
                <div className={`text-3xl font-black ${model.netOfficeProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {fmtINR(model.netOfficeProfit)}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

function BigStat({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="relative overflow-hidden bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-all">
      <div className={`absolute top-0 right-0 w-28 h-28 bg-gradient-to-br ${color} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2`} />
      <div className="p-5 relative">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-3 shadow-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-black text-slate-900 mt-1">{value}</div>
        {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function CategoryCard({ icon: Icon, title, accent, earning, expense, profit, expenseCount, quoteCount }) {
  const a = accent === 'pink'
    ? { bg: 'bg-pink-50', text: 'text-pink-600', badge: 'bg-pink-100 text-pink-700' }
    : { bg: 'bg-indigo-50', text: 'text-indigo-600', badge: 'bg-indigo-100 text-indigo-700' };
  const profitable = profit >= 0;
  return (
    <Card className="border-0 shadow-lg hover:shadow-xl transition-all">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-11 h-11 rounded-xl ${a.bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${a.text}`} />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-slate-800">{title}</h3>
            <div className="flex gap-2 mt-0.5">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${a.badge}`}>{quoteCount} quotes</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700">{expenseCount} expenses</span>
            </div>
          </div>
        </div>
        <div className="space-y-2.5">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Earning</span><span className="font-bold text-blue-700">{fmtINR(earning)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Expense</span><span className="font-bold text-amber-700">{fmtINR(expense)}</span></div>
          <div className="flex justify-between pt-2.5 border-t border-slate-100">
            <span className="font-semibold text-slate-700">Profit</span>
            <span className={`font-black text-lg ${profitable ? 'text-emerald-600' : 'text-red-600'}`}>{fmtINR(profit)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value, muted, bold, highlight }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className={muted ? 'text-white/60' : bold ? 'font-bold text-white' : 'text-white/80'}>{label}</span>
      <span className={`font-bold ${highlight === true ? 'text-emerald-400' : highlight === false ? 'text-red-400' : muted ? 'text-white/70' : 'text-white'}`}>{value}</span>
    </div>
  );
}

export default ProfitabilityDashboard;
